import React from 'react';
import { User, AppState } from '../types';
import SparkleDust from './SparkleDust';
import Typewriter from './Typewriter';

interface HeaderProps {
  onHome: () => void;
  currentView: string;
  onViewChange: (view: AppState) => void;
  user: User | null;
}

const Header: React.FC<HeaderProps> = ({ onHome, currentView, onViewChange, user }) => {
  const isMinimalView = currentView === AppState.LIBRARY || 
                        currentView === AppState.READING_STORY || 
                        currentView === AppState.SHARED_STORY ||
                        currentView === AppState.SHOP ||
                        currentView === AppState.PROFILE;

  return (
    <header className="mb-10 md:mb-16 text-center no-print w-full px-2 relative max-w-5xl mx-auto">
      {user && (
        <div className="flex items-center justify-between mb-10 px-2">
          {/* Magic Tokens Badge */}
          <button 
            onClick={() => onViewChange(AppState.SHOP)}
            className="flex items-center h-14 gap-3 bg-yellow-400/10 border border-yellow-400/20 px-4 rounded-2xl shadow-lg group transition-all active:scale-95 hover:bg-yellow-400/20"
          >
            <span className="text-2xl group-hover:rotate-12 transition-transform">💎</span>
            <div className="text-left">
              <p className="text-[9px] font-black text-yellow-500 uppercase leading-none tracking-widest">Xhevahire</p>
              <p className="text-base font-bold text-yellow-100">{user.tokens}</p>
            </div>
          </button>

          {/* App Name in the middle */}
          {isMinimalView && (
            <div 
              onClick={onHome}
              className="cursor-pointer hover:scale-105 transition-all active:scale-95 flex items-center justify-center h-14 flex-1"
            >
              <h1 className="text-xl md:text-3xl font-bold text-white font-fairytale text-glow-yellow whitespace-nowrap px-4 select-none">
                Përrall<span className="text-amber-400 text-glow-ai">AI</span>me
              </h1>
            </div>
          )}

          {/* Profile Quick Access */}
          <button 
            onClick={() => onViewChange(AppState.PROFILE)}
            className={`flex items-center h-14 gap-3 px-4 rounded-2xl border transition-all active:scale-95 ${
              currentView === AppState.PROFILE 
                ? 'bg-indigo-600 border-indigo-400 text-white shadow-indigo-500/20' 
                : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
            }`}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center font-black text-sm shadow-inner text-white shrink-0">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs md:text-sm font-bold font-comic hidden sm:inline truncate max-w-[100px]">{user.name}</span>
          </button>
        </div>
      )}

      {!isMinimalView && (
        <div className="cursor-pointer group inline-block" onClick={onHome}>
          <h1 className="text-5xl md:text-8xl font-bold text-white font-fairytale tracking-tight text-glow-yellow select-none transition-transform group-hover:scale-[1.02]">
            Përrall<span className="text-amber-400 text-glow-ai">AI</span>me
          </h1>
          <Typewriter 
            text={`Një aplikacion magjik që krijon përralla të personalizuara për fëmijë me personazhet tuaj dhe ilustrime unike.\nMagjia e AI fillon me një përrallë…\nKrijo tënden sot!`}
            className="text-yellow-200/80 mt-4 font-medium text-sm md:text-base italic tracking-wide min-h-[5em] text-center"
            speed={40}
            delay={500}
          />
        </div>
      )}

      {user && (
        <nav className="mt-12 flex justify-center gap-3 md:gap-6 max-w-md mx-auto">
          <button 
            onClick={() => onViewChange(AppState.IDLE)}
            className={`flex-1 sparkle-nav-btn active:scale-95 ${currentView === AppState.IDLE ? 'active' : ''}`}
          >
            <SparkleDust />
            <span className={`text-4xl transition-all duration-300 ${
                currentView === AppState.IDLE 
                  ? 'scale-110 drop-shadow-[0_0_10px_rgba(251,191,36,0.8)]' 
                  : 'opacity-70 grayscale-[0.5] hover:grayscale-0 hover:opacity-100'
              }`}>✨</span>
            <span>Krijo</span>
          </button>
          <button 
            onClick={() => onViewChange(AppState.LIBRARY)}
            className={`flex-1 sparkle-nav-btn active:scale-95 ${currentView === AppState.LIBRARY ? 'active' : ''}`}
          >
            <SparkleDust />
            <span className={`text-4xl transition-all duration-300 ${
                currentView === AppState.LIBRARY 
                  ? 'scale-110 drop-shadow-[0_0_10px_rgba(251,191,36,0.4)]' 
                  : 'opacity-70 grayscale-[0.5] hover:grayscale-0 hover:opacity-100'
              }`}>📚</span>
            <span>Libraria</span>
          </button>
        </nav>
      )}
    </header>
  );
};

export default Header;